(() => {
  if (!customElements.get('accordion-background-media')) {
    class AccordionBackgroundMedia extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.handleImageError = this.handleImageError.bind(this);
        this.querySelectorAll('img').forEach((image) => image.addEventListener('error', this.handleImageError));
      }

      disconnectedCallback() {
        this.querySelectorAll('img').forEach((image) => image.removeEventListener('error', this.handleImageError));
        this.initialized = false;
      }

      handleImageError() {
        this.closest('.accordion-background-layout')?.classList.add('background-media-failed');
      }
    }

    customElements.define('accordion-background-media', AccordionBackgroundMedia);
  }

  if (customElements.get('accordion-background-video')) return;

  class AccordionBackgroundVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.video = document.getElementById(this.dataset.videoId);
      this.button = this.querySelector('[data-accordion-background-video-toggle]');
      this.playIcon = this.button?.querySelector('[data-accordion-background-video-icon="play"]');
      this.pauseIcon = this.button?.querySelector('[data-accordion-background-video-icon="pause"]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.handleToggle = this.toggle.bind(this);
      this.handleStateChange = this.updateControl.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotion.bind(this);
      this.handleIntersection = this.handleIntersect.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMediaError = this.handleMediaError.bind(this);

      if (!this.video || !this.button) {
        this.pause();
        return;
      }

      this.button.addEventListener('click', this.handleToggle);
      this.video.addEventListener('play', this.handleStateChange);
      this.video.addEventListener('pause', this.handleStateChange);
      this.video.addEventListener('error', this.handleMediaError);
      this.reducedMotion.addEventListener('change', this.handleReducedMotionChange);
      this.observer = new IntersectionObserver(this.handleIntersection, {threshold: 0.1});
      this.observer.observe(this);
      window.addEventListener('resize', this.handleVisibilityChange);
      window.addEventListener('mediaChange', this.handleVisibilityChange);
      this.button.disabled = false;
      this.updateControl();
    }

    disconnectedCallback() {
      this.pause();
      this.button?.removeEventListener('click', this.handleToggle);
      this.video?.removeEventListener('play', this.handleStateChange);
      this.video?.removeEventListener('pause', this.handleStateChange);
      this.video?.removeEventListener('error', this.handleMediaError);
      this.reducedMotion?.removeEventListener('change', this.handleReducedMotionChange);
      this.observer?.disconnect();
      window.removeEventListener('resize', this.handleVisibilityChange);
      window.removeEventListener('mediaChange', this.handleVisibilityChange);
      this.initialized = false;
    }

    handleIntersect(entries) {
      this.inViewport = entries[0]?.isIntersecting === true;
      this.updatePlayback();
    }

    toggle() {
      if (this.video.paused) {
        this.setUserPaused(false);
        this.play(true);
      } else {
        this.setUserPaused(true);
        this.pause();
      }
    }

    play(isUserInitiated = false) {
      if (!this.isActive() || !this.video || (!isUserInitiated && (this.reducedMotion.matches || this.isUserPaused()))) return;

      try {
        this.pauseSiblingVideos();
        const playPromise = this.video.play();
        if (playPromise) playPromise.catch(() => this.handleMediaError());
      } catch {
        this.handleMediaError();
      }
    }

    pause() {
      try {
        this.video?.pause();
      } catch {
      }
      this.updateControl();
    }

    handleReducedMotion(event) {
      if (event.matches) this.pause();
    }

    handleVisibilityChange() {
      this.updatePlayback();
    }

    handleMediaError() {
      this.closest('.accordion-background-layout')?.classList.add('background-media-failed');
      this.pause();
    }

    isActive() {
      return this.inViewport && this.getClientRects().length > 0 && getComputedStyle(this).display !== 'none';
    }

    isUserPaused() {
      return this.closest('.accordion-background-layout')?._accordionBackgroundUserPaused === true;
    }

    setUserPaused(userPaused) {
      const backgroundLayout = this.closest('.accordion-background-layout');
      if (backgroundLayout) backgroundLayout._accordionBackgroundUserPaused = userPaused;
    }

    updatePlayback() {
      if (!this.isActive()) {
        this.pause();
        return;
      }

      if (!this.loaded) {
        this.loaded = true;
        this.video.load();
      }
      if (this.dataset.autoplay === 'true' && !this.reducedMotion.matches && !this.isUserPaused()) this.play();
    }

    pauseSiblingVideos() {
      this.closest('.accordion-background-layout')?.querySelectorAll('accordion-background-video').forEach((controller) => {
        if (controller !== this) controller.pause();
      });
    }

    updateControl() {
      if (!this.button || !this.video) return;

      const isPlaying = !this.video.paused;
      this.button.setAttribute('aria-label', isPlaying ? this.dataset.pauseLabel : this.dataset.playLabel);
      this.button.setAttribute('aria-pressed', String(isPlaying));
      this.playIcon.hidden = isPlaying;
      this.pauseIcon.hidden = !isPlaying;
    }
  }

  customElements.define('accordion-background-video', AccordionBackgroundVideo);
})();
